import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { CardComp, ButtonComp } from "../../../components";
import { formatMoney, convertToTitleCase } from "../../../utils";
import { useGetMemberOnlinePendingPaymentsQuery } from "../../../store/member/member-contribution";

function normalizeRows(listData) {
  if (!listData) return [];
  if (Array.isArray(listData)) return listData;
  if (Array.isArray(listData.data)) return listData.data;
  return [];
}

function providerLabel(provider) {
  const p = String(provider || "").toLowerCase();
  if (p === "alatpay") return "ALATPay";
  if (p === "paystack") return "Paystack";
  return convertToTitleCase(p || "Online");
}

/**
 * Pending online (Paystack / ALATPay) attempts so members can reopen verification
 * after leaving the confirmation page.
 */
export default function MemberPendingOnlinePaymentsCard({ flowFilter }) {
  const navigate = useNavigate();
  const { data, isFetching } = useGetMemberOnlinePendingPaymentsQuery({
    status: "pending",
    page: 1,
    limit: 10,
  });

  const rows = useMemo(() => {
    const all = normalizeRows(data).filter(
      (row) => String(row?.status || "").toUpperCase() === "PENDING"
    );
    if (!flowFilter) return all;
    return all.filter((row) => {
      const ref = String(row?.payment_reference || "").toUpperCase();
      if (flowFilter === "contribution") return ref.includes("CONTRIBUTION");
      if (flowFilter === "loan") return ref.includes("LOAN");
      if (flowFilter === "attendance") {
        return ref.includes("ATT_FINE") || ref.includes("FINE");
      }
      return true;
    });
  }, [data, flowFilter]);

  if (isFetching && rows.length === 0) {
    return (
      <CardComp cardClassName="border-0 mb-3">
        <div className="p-3 text-muted small">Checking pending online payments…</div>
      </CardComp>
    );
  }

  if (rows.length === 0) return null;

  return (
    <CardComp cardClassName="border-0 mb-3">
      <div className="p-3">
        <h6 className="fw-bold text-black1 mb-1">Pending online payments</h6>
        <p className="text-muted small mb-3">
          These payments are still waiting for gateway confirmation. Open
          verification again if you already paid, or ask staff to requery.
        </p>
        <div className="d-flex flex-column gap-3">
          {rows.map((row) => (
            <div
              key={row.id}
              className="d-flex flex-wrap justify-content-between align-items-center gap-2 border rounded p-3"
              style={{ borderColor: "#e0e0e0" }}
            >
              <div>
                <div className="fw-semibold text-black1">
                  {formatMoney(Number(row.total_amount || 0).toFixed(2))}
                </div>
                <div className="small text-muted">
                  {providerLabel(row.payment_provider)} ·{" "}
                  {row.created_at
                    ? moment(row.created_at).format("DD-MMM-YYYY HH:mm")
                    : "—"}
                </div>
                <div
                  className="small text-muted"
                  style={{ fontFamily: "monospace", fontSize: "12px" }}
                >
                  {row.payment_reference}
                </div>
              </div>
              <ButtonComp
                type="button"
                btnText="Continue verification"
                btnClassName="bg-primary1 text-white rounded py-2 px-3"
                ButtonInlineStyle={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
                onClick={() =>
                  navigate(
                    `/member/payment/confirmation?reference=${encodeURIComponent(
                      row.payment_reference
                    )}`
                  )
                }
              />
            </div>
          ))}
        </div>
      </div>
    </CardComp>
  );
}
